// Where every one of these links goes unless we are being run against a local
// ss-ui: standalone, framed by a deployed ShipStation, or launched in a way
// that tells us nothing about the host.
const PRODUCTION_APP_ORIGIN = 'https://ship.shipstation.com';

// Kept here rather than in STORAGE_KEYS: that lives in utils/DICTIONARY, and
// importing it would close a module cycle through the route table that reads
// this value at import time.
const LAUNCH_ORIGIN_STORAGE_KEY = 'ss_app_origin';

// The launch URL and the referrer are both set by whoever opened us, so an
// origin is only usable once we know it serves the ShipStation app —
// otherwise anyone who frames or links to us decides where a link that reads
// "ShipStation settings" actually goes.
//
// Only the local dev host qualifies: every deployed ShipStation host reaches
// the same production app, so a launch from one changes nothing and is left to
// fall through to PRODUCTION_APP_ORIGIN. A local ss-ui (e.g.
// https://local.sslocal.com:8080) is the one case where the link has to follow
// the launch host instead.
const LOCAL_APP_HOSTNAME = /(^|\.)sslocal\.com$/;

const originOf = url => {
  try {
    return new URL(url).origin;
  } catch (error) {
    return '';
  }
};

const isTrustedAppOrigin = origin => {
  if (!origin) return false;

  try {
    const { protocol, hostname } = new URL(origin);
    return protocol === 'https:' && LOCAL_APP_HOSTNAME.test(hostname);
  } catch (error) {
    return false;
  }
};

// A reload drops the referrer, so the host we were launched from is worth
// keeping for the life of the tab rather than re-deriving it. Session scope,
// not local: a launch from a dev host should not outlive the tab it happened
// in. Storage can be unavailable — a sandboxed frame, a locked-down browser —
// and a link that falls back to production beats a page that throws.
const rememberedLaunchOrigin = {
  read() {
    try {
      return sessionStorage.getItem(LAUNCH_ORIGIN_STORAGE_KEY);
    } catch (error) {
      return null;
    }
  },
  write(origin) {
    try {
      sessionStorage.setItem(LAUNCH_ORIGIN_STORAGE_KEY, origin);
    } catch (error) {
      // Nothing to do — the origin gets derived again on the next load.
    }
  }
};

// Embedded we are framed by the ShipStation app; in a new tab we were opened
// by it. Either way the host that launched us is the one the user holds a
// session on — which only matters for a local ss-ui, where a link to
// production arrives signed out and cannot be followed back to the app under
// test. A dev host is a run-time fact, so it is read here rather than built in.
function readLaunchOrigin() {
  // Named on the launch URL: the one source a referrer policy cannot strip.
  const declaredOrigin = new URLSearchParams(window.location.search).get(
    'app_origin'
  );

  if (declaredOrigin) return originOf(declaredOrigin);

  // Framed: the frame that holds us, asked directly. Chromium and WebKit only.
  const { ancestorOrigins } = window.location;
  const framingOrigin = ancestorOrigins && ancestorOrigins[0];

  if (framingOrigin) return framingOrigin;

  // Framed on Firefox, or opened as a tab anywhere: the page that put us here.
  // Our own origin means we navigated within ourselves, which says nothing
  // about the host that launched us.
  const referrerOrigin = originOf(document.referrer);

  if (referrerOrigin !== window.location.origin) return referrerOrigin;

  return '';
}

export default function getShipStationAppOrigin() {
  if (typeof window === 'undefined') return PRODUCTION_APP_ORIGIN;

  const launchOrigin = readLaunchOrigin();

  if (isTrustedAppOrigin(launchOrigin)) {
    rememberedLaunchOrigin.write(launchOrigin);
    return launchOrigin;
  }

  const storedOrigin = rememberedLaunchOrigin.read();

  return isTrustedAppOrigin(storedOrigin)
    ? storedOrigin
    : PRODUCTION_APP_ORIGIN;
}
